/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import { atom } from 'jotai';
import type { StyleSpecification } from 'maplibre-gl';
import { getProxiedTileUrl } from '../services/arcgisProxy';

/**
 * Single source of truth for the basemaps offered by the map style picker.
 *
 * Two kinds of basemap are supported:
 *
 * 1. Esri Basemap Styles v2 vector styles (`raster` absent) -- a style path from
 *    `basemapstyles-api.arcgis.com`, fetched and proxied by the server's
 *    arcgis-proxy with the FEMA AGOL token injected. The two families the service
 *    exposes are `arcgis/*` (Esri) and `open/*` (OpenStreetMap-based; the legacy
 *    `osm/*` family is deprecated), both accessible with the standard
 *    `premium:user:basemaps` privilege. Partial styles (`/base`, `/labels`,
 *    `/detail`) are excluded -- they are compositing layers, not basemaps.
 *
 * 2. Raster XYZ/WMTS tile services (`raster` present) -- e.g. USGS The National
 *    Map. Their tiles are routed through the arcgis-proxy `tile` endpoint
 *    (host-allowlisted, server-cached); {@link buildRasterStyle} wraps them in
 *    a minimal MapLibre style.
 *
 * Google Maps tiles are intentionally absent. Google Maps Platform ToS 10.5(e)
 * prohibits using Google map content "in a Maps API Implementation that contains
 * a non-Google map", and the app renders with MapLibre/deck.gl. `arcgis/imagery*`,
 * `open/hybrid*`, and USGS Imagery are the compliant imagery options.
 */
export type BasemapProvider = 'Esri' | 'OpenStreetMap' | 'USGS';

export interface RasterSource {
  /** Upstream XYZ tile template containing MapLibre `{z}`/`{y}`/`{x}` tokens. */
  readonly tileUrl: string;
  /** Source tile size in pixels. */
  readonly tileSize: number;
  /** Max zoom with native tiles; MapLibre overzooms beyond this. */
  readonly maxZoom: number;
  /** Attribution shown in the map's attribution control (provider credit). */
  readonly attribution: string;
}

export interface BasemapOption {
  /** Esri v2 style path (`arcgis/imagery`) or a stable id for a raster source. */
  readonly key: string;
  /** Concise style name shown under the provider group in the picker. */
  readonly label: string;
  /** Source provider, used to group and attribute styles in the picker. */
  readonly provider: BasemapProvider;
  /** Dark basemap -- map overlays (e.g. county labels) must render light. Absent = light. */
  readonly dark?: boolean;
  /** Present for raster (XYZ/WMTS) basemaps; absent for Esri v2 vector styles. */
  readonly raster?: RasterSource;
}

const USGS_TILE_BASE = 'https://basemap.nationalmap.gov/arcgis/rest/services';

export const BASEMAPS: readonly BasemapOption[] = [
  // Esri (arcgis/*)
  { key: 'arcgis/streets-night', label: 'Streets (Night)', provider: 'Esri', dark: true },
  { key: 'arcgis/streets', label: 'Streets', provider: 'Esri' },
  { key: 'arcgis/streets-relief', label: 'Streets with Relief', provider: 'Esri' },
  { key: 'arcgis/navigation', label: 'Navigation', provider: 'Esri' },
  { key: 'arcgis/navigation-night', label: 'Navigation (Night)', provider: 'Esri', dark: true },
  { key: 'arcgis/community', label: 'Community', provider: 'Esri' },
  { key: 'arcgis/outdoor', label: 'Outdoor', provider: 'Esri' },
  { key: 'arcgis/topographic', label: 'Topographic', provider: 'Esri' },
  { key: 'arcgis/terrain', label: 'Terrain', provider: 'Esri' },
  {
    key: 'arcgis/imagery',
    label: 'Satellite Hybrid (Imagery + Labels)',
    provider: 'Esri',
    dark: true,
  },
  { key: 'arcgis/imagery/standard', label: 'Satellite (Imagery)', provider: 'Esri', dark: true },
  { key: 'arcgis/light-gray', label: 'Light Gray Canvas', provider: 'Esri' },
  { key: 'arcgis/dark-gray', label: 'Dark Gray Canvas', provider: 'Esri', dark: true },
  { key: 'arcgis/charted-territory', label: 'Charted Territory', provider: 'Esri' },
  { key: 'arcgis/nova', label: 'Nova', provider: 'Esri', dark: true },

  // OpenStreetMap (open/*)
  { key: 'open/osm-style', label: 'Standard', provider: 'OpenStreetMap' },
  { key: 'open/osm-style-relief', label: 'Standard with Relief', provider: 'OpenStreetMap' },
  { key: 'open/navigation', label: 'Navigation', provider: 'OpenStreetMap' },
  {
    key: 'open/navigation-dark',
    label: 'Navigation (Dark)',
    provider: 'OpenStreetMap',
    dark: true,
  },
  { key: 'open/streets', label: 'Streets', provider: 'OpenStreetMap' },
  { key: 'open/streets-relief', label: 'Streets with Relief', provider: 'OpenStreetMap' },
  { key: 'open/streets-night', label: 'Streets (Night)', provider: 'OpenStreetMap', dark: true },
  // Esri World Imagery base with an OSM-data label/road overlay (the `open/*`
  // family is OSM data hosted by Esri; OSM itself has no satellite imagery).
  {
    key: 'open/hybrid',
    label: 'Satellite (Esri) + OSM Labels',
    provider: 'OpenStreetMap',
    dark: true,
  },
  { key: 'open/light-gray', label: 'Light Gray Canvas', provider: 'OpenStreetMap' },
  { key: 'open/dark-gray', label: 'Dark Gray Canvas', provider: 'OpenStreetMap', dark: true },

  // USGS The National Map (public domain, no token)
  {
    key: 'usgs/imagery',
    label: 'Imagery (Aerial)',
    provider: 'USGS',
    dark: true,
    raster: {
      tileUrl: `${USGS_TILE_BASE}/USGSImageryOnly/MapServer/tile/{z}/{y}/{x}`,
      tileSize: 256,
      maxZoom: 16,
      attribution: 'USDA NAIP, USGS The National Map: Orthoimagery',
    },
  },
  {
    key: 'usgs/imagery-topo',
    label: 'Imagery + Topo',
    provider: 'USGS',
    dark: true,
    raster: {
      tileUrl: `${USGS_TILE_BASE}/USGSImageryTopo/MapServer/tile/{z}/{y}/{x}`,
      tileSize: 256,
      maxZoom: 16,
      attribution: 'USGS The National Map: Orthoimagery and US Topo',
    },
  },
  {
    key: 'usgs/topo',
    label: 'Topographic',
    provider: 'USGS',
    raster: {
      tileUrl: `${USGS_TILE_BASE}/USGSTopo/MapServer/tile/{z}/{y}/{x}`,
      tileSize: 256,
      maxZoom: 16,
      attribution: 'USGS The National Map: US Topo',
    },
  },
  {
    key: 'usgs/hydro',
    label: 'Hydrography',
    provider: 'USGS',
    raster: {
      tileUrl: `${USGS_TILE_BASE}/USGSHydroCached/MapServer/tile/{z}/{y}/{x}`,
      tileSize: 256,
      maxZoom: 16,
      attribution: 'USGS The National Map: National Hydrography Dataset',
    },
  },
];

/** Provider order for grouping in the picker. */
export const BASEMAP_PROVIDERS: readonly BasemapProvider[] = ['Esri', 'OpenStreetMap', 'USGS'];

/** Default basemap key, used as the initial picker selection. */
export const DEFAULT_BASEMAP_KEY = 'arcgis/streets-night';

const BASEMAP_BY_KEY: ReadonlyMap<string, BasemapOption> = new Map(BASEMAPS.map(b => [b.key, b]));

export function getBasemap(key: string): BasemapOption | undefined {
  return BASEMAP_BY_KEY.get(key);
}

/** Whether overlays drawn on top of this basemap need light-colored text for contrast. */
export function isDarkBasemap(key: string): boolean {
  return getBasemap(key)?.dark ?? false;
}

/**
 * Public URL of a basemap's pre-rendered thumbnail (captured over FEMA HQ).
 * The key's `/` is flattened to `-` for the filename; assets live in
 * `apps/web/public/basemap-thumbnails/` and are regenerated by
 * `scripts/generate-basemap-thumbnails.md`.
 */
export function basemapThumbnailUrl(key: string): string {
  return `/basemap-thumbnails/${key.replace(/\//g, '-')}.jpg`;
}

/** Build a minimal MapLibre style for a raster basemap, proxying its tiles. */
export function buildRasterStyle(source: RasterSource): StyleSpecification {
  return {
    version: 8,
    sources: {
      basemap: {
        type: 'raster',
        tiles: [getProxiedTileUrl(source.tileUrl)],
        tileSize: source.tileSize,
        maxzoom: source.maxZoom,
        attribution: source.attribution,
      },
    },
    layers: [
      // Opaque backdrop: some caches (e.g. USGS Hydrography) have transparent
      // tiles, which would otherwise let the page behind the map bleed through.
      { id: 'background', type: 'background', paint: { 'background-color': '#ffffff' } },
      { id: 'basemap', type: 'raster', source: 'basemap' },
    ],
  };
}

/**
 * Esri styles that do not accept the `worldview` query param (they have no
 * disputed-boundary variants). Sending one makes the v2 service return a 400.
 */
const ESRI_NO_WORLDVIEW: ReadonlySet<string> = new Set([
  'arcgis/imagery/standard',
  'arcgis/charted-territory',
]);

/**
 * Whether the `worldview` query param may be sent for this basemap style.
 *
 * Worldview is an Esri-only feature: every `open/*` (OpenStreetMap) style
 * rejects it with a 400, and a few Esri imagery/artistic styles opt out too.
 */
export function basemapSupportsWorldview(key: string): boolean {
  const basemap = getBasemap(key);
  if (!basemap) return true;
  return basemap.provider === 'Esri' && !ESRI_NO_WORLDVIEW.has(key);
}

/** Currently selected basemap style path. */
export const mapStyleAtom = atom<string>(DEFAULT_BASEMAP_KEY);
